<template>
	<v-card
		variant="outlined"
		class="resource-card-shell"
		min-width="300px"
		data-test="task-card"
	>
		<div
			v-if="taskData"
			class="resource-card"
		>
			<!-- Header: Task ID, Core, Status -->
			<div class="resource-card__header sgnl-paragraph-s-regular">
				<div class="resource-card__header-info">
					<Task20 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
					<v-chip
						variant="outlined"
						class="badge-muted copy-id-chip"
						data-test="task-card-task-id-value"
					>
						Task ID: <span class="truncated">{{ util.shortenID(taskData.TaskID) }}</span>
						<CopyToClipboardButton
							:value-to-copy="taskData.TaskID"
							value-name="Task ID"
						/>
					</v-chip>
					<SkeletonBlock
						v-if="!buildData"
						width="95px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
					<template v-else-if="taskCoreSpec">
						<span
							v-if="!$vuetify.display.mobile"
							class="sgnl-text-color-secondary"
							>on</span
						>
						<v-tooltip
							open-delay="500"
							content-class="tooltip-transparent"
						>
							<template v-slot:activator="{ props }">
								<v-chip
									v-bind="props"
									variant="outlined"
									class="sgnl-font-weight-medium"
								>
									<Chip16 />
									{{ taskCoreName }}
								</v-chip>
							</template>
							<ActiveCoreTooltip :active-core="taskCoreSpec" />
						</v-tooltip>
					</template>
				</div>
				<div
					class="resource-card__status status-label sgnl-text-color-secondary"
					data-test="task-card-task-status-value"
				>
					<v-tooltip
						bottom
						v-if="remoteError"
					>
						<template v-slot:activator="{ props }">
							<Warning16
								class="sgnl-icon-color-warning"
								v-bind="props"
							/>
						</template>
						<span>{{ remoteErrorMessage }}</span>
					</v-tooltip>
					<span :class="['status-dot', statusDotClass]" />
					<span>{{ taskData.Status }}</span>
				</div>
			</div>
			<div class="task-card-body resource-card__description">
				<SkeletonBlock
					v-if="!buildData"
					width="50%"
					height="var(--sgnl-unit-24)"
					radius="var(--sgnl-border-radius-6)"
				/>
				<SourceReference
					v-else
					:repo="cardRepo"
					:source-code="cardSourceCode"
					:show-build-directory="taskData.TaskID != null"
				/>
				<!-- Error message -->
				<div
					v-if="taskStatusMessageOnError"
					class="sgnl-flex sgnl-align-center sgnl-gap-8"
				>
					<WarningAlt16 class="task-error-icon" />
					<v-tooltip
						bottom
						:disabled="!showErrorDetailsTooltip"
					>
						<template v-slot:activator="{ props }">
							<code
								class="task-error-message sgnl-text-color-error"
								v-bind="props"
							>
								{{ taskStatusMessageOnError }}
							</code>
						</template>
						<span>Find more information related to this error on 'Show Error Details'</span>
					</v-tooltip>
				</div>
				<!-- Stuck notice -->
				<StuckNotice
					v-else-if="taskStuckMessage"
					:message="taskStuckMessage"
				/>
				<!-- Info badges -->
				<div class="sgnl-flex sgnl-flex-wrap info-badges">
					<!-- Duration -->
					<v-tooltip
						location="bottom"
						content-class="tooltip-transparent"
					>
						<template v-slot:activator="{ props }">
							<v-chip
								variant="text"
								v-bind="props"
							>
								<Timer16 />
								<template v-if="isTaskActive(taskData.Status)"> Task in progress </template>
								<template v-else>
									{{ runDuration }}
								</template>
							</v-chip>
						</template>
						<TaskStatusTransitionTooltip :taskStatusTransitions="taskData.StatusTransitions" />
					</v-tooltip>
					<!-- Timestamp -->
					<v-chip variant="text">
						<Calendar16 />
						<template v-if="taskData.Status === TaskStatusE.Accepted || !taskData.StartedAt">
							Queued {{ createdTimestamp }}
						</template>
						<template v-else>{{ startTimestamp }}</template>
						<template v-if="!isTaskActive(taskData.Status) && taskData.FinishedAt">
							<ArrowRight16 />
							{{ endTimestamp }}
						</template>
					</v-chip>
					<!-- Execution time (completed only) -->
					<v-chip
						v-if="taskData.Status === TaskStatusE.Completed && executionTimeString"
						variant="text"
					>
						<Timer16 />
						C0 Execution Time: {{ executionTimeString }}
					</v-chip>
					<!-- Dynamic instructions (completed only) -->
					<v-chip
						v-if="taskData.Status === TaskStatusE.Completed && dynamicInstructions"
						variant="text"
					>
						<NotificationCounter16 />
						{{ dynamicInstructions }} dynamic instruction credits
					</v-chip>
					<!-- Active task progress -->
					<v-progress-circular
						v-if="isTaskActive(taskData.Status)"
						indeterminate
						size="16"
						width="2"
						:color="util.taskStatusColor(taskData?.Status)"
					/>
				</div>
				<!-- Arguments -->
				<div
					v-if="taskArguments"
					class="sgnl-flex sgnl-gap-8"
					:class="inTaskDetail ? 'sgnl-align-start' : 'sgnl-align-center'"
				>
					<Terminal16 class="argument-icon" />
					<div
						ref="argumentArea"
						code-area
						textarea-narrowLineHeight
						:class="['argument-area', inTaskDetail ? 'argument-area--resizable' : 'argument-area--inline']"
					>
						<span
							ref="argumentText"
							class="argument-text"
							>{{ taskArguments }}</span
						>
						<v-btn
							v-if="!inTaskDetail && argumentsOverflow"
							variant="text"
							size="small"
							density="compact"
							class="argument-see-more"
							data-test="task-card-arguments-see-more-button"
							@click="openTaskDetails"
						>
							See more
						</v-btn>
					</div>
				</div>
			</div>

			<!-- Actions -->
			<div
				v-if="isTaskActive(taskData.Status)"
				class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8"
			>
				<TooltipButton
					v-if="!inTaskDetail"
					variant="flat"
					color="primary"
					min-width="0px"
					@click="openTaskDetails()"
					btn-text="Show Details"
					tooltipText="Show Details"
					data-test="task-card-open-details-button"
				/>
				<v-spacer />
				<v-btn
					variant="outlined"
					color="error"
					size="small"
					:loading="cancel.isCancellingTask(taskData?.TaskID)"
					@click="cancelActiveTask"
					data-test="task-card-cancel-button"
				>
					<CloseFilled16 />
					Cancel Task
				</v-btn>
			</div>
			<div
				v-else
				class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8"
			>
				<TooltipButton
					v-if="!inTaskDetail"
					variant="flat"
					color="primary"
					min-width="0px"
					@click="openTaskDetails()"
					:btn-text="
						$vuetify.display.mobile
							? 'Details'
							: taskStatusMessageOnError
								? 'Show Error Details'
								: 'Show Details'
					"
					:tooltipText="taskStatusMessageOnError ? 'Show Error Details' : 'Show Details'"
					data-test="task-card-open-details-button"
				/>
				<v-spacer />
				<v-tooltip v-if="showDeleteTaskButton">
					<template v-slot:activator="{ props }">
						<v-btn
							variant="outlined"
							color="error"
							@click="openRemoveTaskDialog()"
							data-test="task-card-open-remove-button"
							v-bind="props"
						>
							<TrashCan16 />
						</v-btn>
					</template>
					Remove task from history
				</v-tooltip>
			</div>
			<TasksRemoveDialog
				:show-dialog="removeTaskDialog"
				@closeRemoveTaskDialog="closeRemoveTaskDialog"
				@removeTaskSuccess="removeTaskSuccess"
				:taskID="taskID"
			/>
		</div>
	</v-card>
</template>

<script lang="ts">
import { PropType, defineComponent, provide, toRef } from "vue";
import { useCancelWithFeedback, CancelFeedbackKey } from "@/composables/useCancelWithFeedback";
import { useStuckResourceMessage } from "@/composables/useStuckResourceMessage";

// Types
import { TaskStatus, TaskStatusE, Task, isTaskActive, isTaskFail } from "@/types/api/tasks";

// Libraries
import moment from "moment";

// Utilities
import * as util from "@/js/utilities";

// Components
import TooltipButton from "@/components/Common/TooltipButton.vue";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import StuckNotice from "@/components/Common/StuckNotice.vue";
import ActiveCoreTooltip from "@/components/Repositories/ActiveCoreTooltip.vue";
import TaskStatusTransitionTooltip from "@/components/TaskManager/TaskStatusTransitionTooltip.vue";
import TasksRemoveDialog from "@/components/TaskManager/TaskRemoveDialog.vue";
import SourceReference from "@/components/TaskManager/SourceReference.vue";

// Carbon icons
import Task20 from "@carbon/icons-vue/es/task/20";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";
import Calendar16 from "@carbon/icons-vue/es/calendar/16";
import Timer16 from "@carbon/icons-vue/es/timer/16";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import CloseFilled16 from "@carbon/icons-vue/es/close--filled/16";
import Warning16 from "@carbon/icons-vue/es/warning/16";
import Terminal16 from "@carbon/icons-vue/es/terminal/16";
import WarningAlt16 from "@carbon/icons-vue/es/warning--alt/16";
import NotificationCounter16 from "@carbon/icons-vue/es/notification-counter/16";

// Stores
import { useTasksStore } from "@/stores/tasks";
import { useRootStore } from "@/stores/root";
import { CoreSpecs } from "@/types/api/cores";
import { Build } from "@/types/api/builds";
import { useBuildsStore } from "@/stores/builds";
import { Repository } from "@/types/api/repositories";
import { SourceCode } from "@/types/api/sourceCode";
import { useCoresStore } from "@/stores/cores";

// Global variables
const AsTimeStamp_19900101 = 631152000;

type ComponentData = {
	remoteError: boolean;
	remoteErrorMessage: string | undefined;
	removeTaskDialog: boolean;
	buildData: Build | undefined;
	argumentsOverflow: boolean;
	argumentsResizeObserver: ResizeObserver | undefined;
};

export default defineComponent({
	name: "TaskCard",
	components: {
		TooltipButton,
		CopyToClipboardButton,
		SourceReference,
		SkeletonBlock,
		StuckNotice,
		ActiveCoreTooltip,
		TaskStatusTransitionTooltip,
		TasksRemoveDialog,
		Task20,
		TrashCan16,
		Chip16,
		Calendar16,
		Timer16,
		ArrowRight16,
		CloseFilled16,
		Warning16,
		WarningAlt16,
		Terminal16,
		NotificationCounter16,
	},
	emits: ["removeTaskSuccess"],
	props: {
		taskID: { type: String, required: true },
		taskData: { type: Object as PropType<Task>, required: false, default: undefined },
		showDeleteTaskButton: { type: Boolean, default: false },
		showErrorDetailsTooltip: { type: Boolean, default: true },
	},
	data: (): ComponentData => ({
		remoteError: false,
		remoteErrorMessage: undefined,
		removeTaskDialog: false,
		buildData: undefined,
		argumentsOverflow: false,
		argumentsResizeObserver: undefined,
	}),
	setup(props) {
		const tasksStore = useTasksStore();
		const buildsStore = useBuildsStore();
		const coresStore = useCoresStore();
		const rootStore = useRootStore();

		const cancel = useCancelWithFeedback();
		provide(CancelFeedbackKey, cancel);

		const taskStuckMessage = useStuckResourceMessage(toRef(props, "taskData"), "task");

		return {
			tasksStore,
			buildsStore,
			coresStore,
			rootStore,
			TaskStatusE,
			isTaskActive,
			util,
			cancel,
			taskStuckMessage,
		};
	},
	watch: {
		taskData: {
			immediate: true,
			handler: async function (newVal) {
				if (!newVal) return;

				const buildID = newVal.BuildID;
				if (!buildID) return;

				const bd = await this.buildsStore.getBuildByIdOrFetch(buildID);
				if (!bd) return;
				this.buildData = bd;
			},
		},
		"taskData.Status"(newStatus: TaskStatus) {
			if (!this.taskData) return;
			this.cancel.handleTaskStatusUpdate(this.taskData.TaskID, newStatus);
		},
		taskArguments(newVal, oldVal) {
			this.$nextTick(() => {
				this.checkArgumentsOverflow();
				// Re-observe only when the v-if'd argument element is (re)mounted.
				if (newVal && !oldVal) this.observeArgumentsOverflow();
			});
		},
		inTaskDetail() {
			this.$nextTick(() => this.checkArgumentsOverflow());
		},
	},

	methods: {
		async cancelActiveTask() {
			await this.cancel.cancelTask(this.taskData?.TaskID);
		},
		normalizeTimestamp(timestamp: number): number {
			return timestamp > 2000000000 ? timestamp : timestamp * 1000;
		},

		secondsSinceEpochToDateString(timestamp: number) {
			//moment requires timestamp in milliseconds
			return moment(this.normalizeTimestamp(timestamp)).calendar(null, {
				sameDay: "[Today]",
				nextDay: "[Tomorrow]",
				nextWeek: "dddd",
				lastDay: "[Yesterday]",
				lastWeek: "[Last] dddd",
				sameElse: "DD/MM/YYYY",
			});
		},
		openTaskDetails() {
			//@ts-ignore
			this.$posthog?.capture("show_task_details_button_clicked");

			// @ts-ignore FIXME: For some reason TS does not like this redirect
			this.$router.push({
				name: "TaskDetail",
				params: { id: this.taskData?.TaskID, initialTaskData: this.taskData?.TaskID },
			});
		},
		checkArgumentsOverflow() {
			const el = this.$refs.argumentText as HTMLElement | undefined;
			const next = !!el && el.scrollWidth > el.clientWidth;
			if (next !== this.argumentsOverflow) this.argumentsOverflow = next;
		},
		observeArgumentsOverflow() {
			this.argumentsResizeObserver?.disconnect();
			const el = this.$refs.argumentArea as HTMLElement | undefined;
			if (!el || typeof ResizeObserver === "undefined") return;
			this.argumentsResizeObserver = new ResizeObserver(() => this.checkArgumentsOverflow());
			this.argumentsResizeObserver.observe(el);
		},
		openRemoveTaskDialog() {
			this.removeTaskDialog = true;
		},
		closeRemoveTaskDialog() {
			this.removeTaskDialog = false;
		},
		removeTaskSuccess() {
			//@ts-ignore
			this.$posthog?.capture("task_metadata_deleted");
			this.$emit("removeTaskSuccess");
		},
	},
	computed: {
		statusDotClass(): string {
			const colorMap: Record<string, string> = {
				success: "status-dot--success",
				warning: "status-dot--warning",
				error: "status-dot--error",
				info: "status-dot--informal",
			};
			const color = util.taskStatusColor(this.taskData?.Status);
			return colorMap[color] || "status-dot--informal";
		},
		taskBuild(): Build | null {
			const buildID = this.taskData?.BuildID;
			if (!buildID) return null;
			return this.buildsStore.getBuildByID(buildID) ?? null;
		},
		cardSourceCode(): SourceCode | null {
			const build = this.buildData;
			if (!build) return null;
			if (build.Application.Type == "SourceCode") {
				return build.Application.SourceCode as SourceCode;
			}
			return null;
		},
		cardRepo(): Repository | null {
			const build = this.buildData;
			if (!build) return null;
			if (build.Application.Type == "Repository") {
				return build.Application.Repository as Repository;
			}
			return null;
		},
		taskStatusMessageOnError: function (): string | null {
			if (this.taskData && !isTaskFail(this.taskData.Status)) {
				return null;
			}
			const statusTransition =
				this.taskData && this.taskData.StatusTransitions.filter((st) => st.Status == this.taskData?.Status)[0];

			// Kea-cloud errors are formatted as `ErrXXX: Error message.`, where `ErrXXX` is an enum-like identifier.
			// This code checks if the message contains a colon, splits it, and returns the error message without the prefix.
			if (statusTransition && statusTransition.Message?.includes(":")) {
				const messageParts = statusTransition.Message.split(":");
				return messageParts.slice(1).join(":").trim() || null;
			}
			return statusTransition?.Message ?? null;
		},
		runDuration(): string {
			const secondsOnly = true;
			const AsTimeStamp_19900101 = 631152000;

			if (!this.taskData || !this.taskData?.CreatedAt || !this.taskData?.FinishedAt) {
				return "---";
			}
			const startedAtSeconds =
				this.taskData.CreatedAt < 1e11 ? this.taskData.CreatedAt : Math.floor(this.taskData.CreatedAt / 1000);
			const finishedAtSeconds =
				this.taskData.FinishedAt < 1e11
					? this.taskData.FinishedAt
					: Math.floor(this.taskData.FinishedAt / 1000);

			if (startedAtSeconds <= AsTimeStamp_19900101 || finishedAtSeconds <= AsTimeStamp_19900101) {
				return "---";
			}

			const finishTime = moment(util.convertToMilliseconds(this.taskData.FinishedAt));
			const startTime = moment(util.convertToMilliseconds(this.taskData.CreatedAt));

			if (finishTime.isValid() && startTime.isValid()) {
				const duration = finishTime.diff(startTime);
				if (duration < 1000) {
					// less than a second
					return secondsOnly ? "< 1s" : `${duration}ms`;
				} else if (duration < 60000) {
					// less than a minute
					return `${Math.floor(duration / 1000)}s` + (secondsOnly ? "" : ` ${duration % 1000}ms`);
				} else {
					// minutes and seconds
					return `${Math.floor(duration / 60000)}m ${Math.floor((duration % 60000) / 1000)}s`;
				}
			} else {
				return "---";
			}
		},
		executionTimeString: function (): string {
			if (!this.taskData || !this.taskData?.Stats || !this.taskData?.Stats.ProcessorTime) {
				return "";
			}

			return util.usDurationToString(this.taskData?.Stats.ProcessorTime * 1000_000);
		},
		dynamicInstructions: function (): string {
			if (!this.taskData || !this.taskData?.Stats || !this.taskData?.Stats.DynamicInstructions) {
				return "";
			}

			return util.toStringWithMagnitude(this.taskData.Stats.DynamicInstructions);
		},
		startTimestamp: function (): string {
			if (!this.taskData?.StartedAt || this.taskData.StartedAt <= AsTimeStamp_19900101) {
				return "---";
			}
			return `${this.secondsSinceEpochToDateString(this.taskData.StartedAt)}, ${moment(
				this.normalizeTimestamp(this.taskData.StartedAt),
			).format("HH:mm:ss")}`;
		},
		createdTimestamp: function (): string {
			if (!this.taskData?.CreatedAt || this.taskData.CreatedAt <= AsTimeStamp_19900101) {
				return "---";
			}
			return `${this.secondsSinceEpochToDateString(this.taskData.CreatedAt)}, ${moment(
				this.normalizeTimestamp(this.taskData.CreatedAt),
			).format("HH:mm:ss")}`;
		},
		endTimestamp: function (): string {
			if (!this.taskData) {
				return "---";
			}
			if (
				moment(this.normalizeTimestamp(this.taskData.StartedAt)).isSame(
					this.normalizeTimestamp(this.taskData.FinishedAt),
					"day",
				)
			) {
				return moment(this.normalizeTimestamp(this.taskData.FinishedAt)).format("HH:mm:ss");
			} else {
				return `${this.secondsSinceEpochToDateString(this.taskData.FinishedAt)}, ${moment(
					this.normalizeTimestamp(this.taskData.FinishedAt),
				).format("HH:mm:ss A")}`;
			}
		},
		projectSourceIcon: function (): string {
			return util.projectSourceIcon(this.buildData?.Application.Type ?? "");
		},
		inTaskDetail(): boolean {
			return this.$route.name == "TaskDetail";
		},
		taskCoreName(): string {
			const buildID = this.taskData?.BuildID;
			if (!buildID) {
				return "Core";
			}
			const build = this.buildsStore.getBuildByID(buildID);
			const coreSpecs = build?.BuildCoreSpecs;
			if (!coreSpecs) {
				return "Core";
			}
			const defaultCores = this.coresStore.allDefaultCores;
			const chosenCore = defaultCores.find((dc) => {
				const sameClass = dc.Class === coreSpecs.Class;
				const samePrecision = dc.Precision === coreSpecs.Precision;
				const sameMemory = dc.MemorySize === coreSpecs.MemorySize;
				const sameMicroarch = dc.Microarchitecture === coreSpecs.Microarchitecture;
				const sameCorrelation = dc.CorrelationTracking === coreSpecs.CorrelationTracking;

				return sameClass && samePrecision && sameMemory && sameMicroarch && sameCorrelation;
			});
			if (!chosenCore) {
				return "Custom Core";
			}
			return chosenCore?.Name;
		},
		taskCoreSpec(): CoreSpecs | undefined {
			const buildID = this.taskData?.BuildID;
			if (!buildID) {
				return;
			}
			const build = this.buildsStore.getBuildByID(buildID);
			const coreSpecs = build?.BuildCoreSpecs;
			return coreSpecs as CoreSpecs;
		},
		taskArguments(): string | undefined {
			return this.taskData?.Arguments;
		},
	},
	async beforeMount() {
		if (!this.taskData) return;
		const buildID = this.taskData.BuildID;
		if (!buildID) return;
		const bd = await this.buildsStore.getBuildByIdOrFetch(buildID);
		if (!bd) return;
		this.buildData = bd;
	},
	mounted() {
		this.$nextTick(() => {
			this.checkArgumentsOverflow();
			this.observeArgumentsOverflow();
		});
	},
	beforeUnmount() {
		this.argumentsResizeObserver?.disconnect();
	},
});
</script>

<style lang="scss" scoped>
.truncated {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 150px;
}
.task-card-body {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}
.task-error-message {
	font-size: var(--sgnl-font-size-paragraph-s);
	background: var(--sgnl-alert-error-background-primary);
	border-radius: var(--sgnl-border-radius-6);
	padding: var(--sgnl-space-4) var(--sgnl-space-8);
}
.task-error-icon {
	color: var(--sgnl-alert-error-icon);
}
.argument-icon {
	flex-shrink: 0;
	color: var(--sgnl-default-secondary-icon);
}
.argument-area {
	width: 100%;
	font-size: var(--sgnl-font-size-caption);
	color: var(--sgnl-default-secondary-text);
	padding: var(--sgnl-space-4);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
}
.argument-area--inline {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-4);
	min-width: 0;
}
.argument-area--inline .argument-text {
	flex: 1 1 0;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.argument-area--resizable {
	height: 6em;
	resize: vertical;
	overflow: auto;
}
.argument-area .argument-see-more {
	flex: 0 0 auto;
	min-width: 0;
	min-height: 0;
	height: auto;
	padding: 0 var(--sgnl-space-4);
}
.argument-area .argument-see-more :deep(.v-btn__content) {
	color: var(--sgnl-brand-text);
	font-family: var(--sgnl-font-family-text);
	font-size: var(--sgnl-font-size-caption);
	line-height: var(--sgnl-font-line-height-caption);
	text-transform: none;
	letter-spacing: 0;
}
.status-label {
	text-align: center;
	font-size: var(--sgnl-font-size-caption);
	font-style: normal;
	font-weight: 500;
	line-height: var(--sgnl-font-line-height-caption);
}
.status-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	flex-shrink: 0;
}
.status-dot--success {
	background-color: var(--sgnl-alert-success-background);
}
.status-dot--warning {
	background-color: var(--sgnl-alert-warning-background);
}
.status-dot--error {
	background-color: var(--sgnl-alert-error-background);
}
.status-dot--informal {
	background-color: var(--sgnl-alert-info-background);
}

@media (max-width: 599px) {
	:deep(.v-spacer) {
		display: none;
	}
}
</style>
